import { useState, useMemo } from 'react'
import { Upload, Button, Table, Alert, message, Space, Typography, Modal, Select, Input } from 'antd'
import { InboxOutlined, ReloadOutlined, SaveOutlined, CheckCircleOutlined } from '@ant-design/icons'
import type { UploadProps } from 'antd'
import { uploadStructure, saveStructure } from '@/api/etl'
import { usePeriods, useCheckPeriod } from '@/hooks/useRefOtdFilials'
import type { StructureUploadResponse, StructureSaveResponse } from '@/types/etl'

const { Dragger } = Upload
const { Title, Text } = Typography

// Генерация списка месяцев для нового периода
const generateMonthOptions = () => {
  const months = [
    { value: '01', label: 'Январь' },
    { value: '02', label: 'Февраль' },
    { value: '03', label: 'Март' },
    { value: '04', label: 'Апрель' },
    { value: '05', label: 'Май' },
    { value: '06', label: 'Июнь' },
    { value: '07', label: 'Июль' },
    { value: '08', label: 'Август' },
    { value: '09', label: 'Сентябрь' },
    { value: '10', label: 'Октябрь' },
    { value: '11', label: 'Ноябрь' },
    { value: '12', label: 'Декабрь' },
  ]
  return months
}

export default function UploadStructure() {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploadResult, setUploadResult] = useState<StructureUploadResponse | null>(null)
  const [saveResult, setSaveResult] = useState<StructureSaveResponse | null>(null)

  // Period selection
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null)
  const [isNewPeriod, setIsNewPeriod] = useState(false)
  const [newMonth, setNewMonth] = useState<string>('01')
  const [newYear, setNewYear] = useState<string>('26')
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [confirmReplaceOpen, setConfirmReplaceOpen] = useState(false)

  // Fetch existing periods
  const { data: periodsData } = usePeriods()
  const periods = periodsData?.periods || []

  // Check if selected period has data
  const periodToCheck = isNewPeriod ? `${newMonth}${newYear}` : selectedPeriod
  const { data: periodCheck } = useCheckPeriod(periodToCheck)

  const effectivePeriod = useMemo(() => {
    return isNewPeriod ? `${newMonth}${newYear}` : selectedPeriod
  }, [isNewPeriod, newMonth, newYear, selectedPeriod])

  const handleUpload = async (file: File) => {
    setLoading(true)
    setSaveResult(null)
    setSelectedPeriod(null)
    setIsNewPeriod(false)
    try {
      const result = await uploadStructure(file)
      setUploadResult(result)

      if (result.success) {
        message.success(`Загружено ${result.rows_count} отделений (только уровень "отделение")`)
      } else {
        message.warning('Загрузка завершена с ошибками')
      }
    } catch (error: any) {
      message.error(`Ошибка загрузки: ${error.response?.data?.detail || error.message}`)
    } finally {
      setLoading(false)
    }
    return false
  }

  const handleSaveClick = () => {
    setSaveModalOpen(true)
  }

  const handlePeriodConfirm = () => {
    if (!effectivePeriod) {
      message.error('Выберите период')
      return
    }

    setSaveModalOpen(false)

    // Check if period has existing data
    if (periodCheck?.exists) {
      setConfirmReplaceOpen(true)
    } else {
      doSave(false)
    }
  }

  const doSave = async (replaceExisting: boolean) => {
    if (!uploadResult || !effectivePeriod) return

    setSaving(true)
    setConfirmReplaceOpen(false)
    try {
      const result = await saveStructure({
        period: effectivePeriod,
        rows: uploadResult.rows,
        replace_existing: replaceExisting,
      })
      setSaveResult(result)

      if (result.success) {
        message.success(`Сохранено ${result.created_count} записей за период ${effectivePeriod}`)
      } else {
        message.warning(`Сохранено ${result.created_count}, ошибок: ${result.errors.length}`)
      }
    } catch (error: any) {
      message.error(`Ошибка сохранения: ${error.response?.data?.detail || error.message}`)
    } finally {
      setSaving(false)
    }
  }

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: '.xlsx',
    beforeUpload: handleUpload,
    showUploadList: false,
    disabled: loading,
  }

  const handleReset = () => {
    setUploadResult(null)
    setSaveResult(null)
    setSelectedPeriod(null)
    setIsNewPeriod(false)
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id_otd',
      key: 'id_otd',
      width: 80,
      fixed: 'left' as const,
    },
    {
      title: 'Полное название отделения',
      dataIndex: 'code_otd_name_full',
      key: 'code_otd_name_full',
      ellipsis: true,
      width: 250,
    },
    {
      title: 'Подразделение',
      dataIndex: 'podr_name_short',
      key: 'podr_name_short',
      ellipsis: true,
      width: 200,
    },
    {
      title: 'Адрес',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
      width: 250,
    },
    {
      title: 'Руководитель',
      dataIndex: 'manager',
      key: 'manager',
      ellipsis: true,
      width: 180,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const isActive = status === 'Активно'
        return <Text type={isActive ? 'success' : 'secondary'}>{status}</Text>
      },
    },
  ]

  // Period options for select
  const periodOptions = [
    ...periods.map((p) => ({
      value: p.period,
      label: `${p.period_label} (${p.count} записей)`,
    })),
    { value: '__new__', label: '+ Добавить новый период' },
  ]

  return (
    <div>
      {!uploadResult ? (
        <Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Нажмите или перетащите файл для загрузки
          </p>
          <p className="ant-upload-hint">
            Поддерживается формат .xlsx (Отчёт "Структура подразделения")
          </p>
          <p className="ant-upload-hint">
            <Text type="secondary">Загружаются только записи с уровнем "отделение"</Text>
          </p>
        </Dragger>
      ) : (
        <div>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Результат сохранения */}
            {saveResult && (
              <Alert
                message={saveResult.success ? 'Успешно сохранено' : 'Сохранено с ошибками'}
                description={
                  <Space direction="vertical">
                    <Text>Создано записей: {saveResult.created_count}</Text>
                    <Text>Пропущено: {saveResult.skipped_count}</Text>
                    {saveResult.errors.length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {saveResult.errors.slice(0, 5).map((e, i) => (
                          <li key={i}><Text type="danger">{e}</Text></li>
                        ))}
                        {saveResult.errors.length > 5 && (
                          <li><Text type="secondary">...и ещё {saveResult.errors.length - 5}</Text></li>
                        )}
                      </ul>
                    )}
                  </Space>
                }
                type={saveResult.success ? 'success' : 'warning'}
                showIcon
                icon={saveResult.success ? <CheckCircleOutlined /> : undefined}
              />
            )}

            {/* Ошибки загрузки */}
            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <Alert
                message="Предупреждения при загрузке"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {uploadResult.errors.slice(0, 5).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                }
                type="warning"
                showIcon
              />
            )}

            {/* Заголовок и кнопки */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={4} style={{ margin: 0 }}>
                Загружено отделений: {uploadResult.rows_count}
              </Title>
              <Space>
                {!saveResult && (
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={handleSaveClick}
                  >
                    Сохранить в базу
                  </Button>
                )}
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                >
                  Загрузить другой файл
                </Button>
              </Space>
            </div>

            {/* Таблица */}
            <Table
              columns={columns}
              dataSource={uploadResult.rows}
              rowKey="id_otd"
              pagination={{
                defaultPageSize: 50,
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100', '200'],
                showTotal: (total, range) => `${range[0]}-${range[1]} из ${total}`,
              }}
              scroll={{ x: 1200 }}
              size="small"
            />
          </Space>
        </div>
      )}

      {/* Modal выбора периода */}
      <Modal
        title="Выберите период для сохранения"
        open={saveModalOpen}
        onOk={handlePeriodConfirm}
        onCancel={() => setSaveModalOpen(false)}
        okText="Продолжить"
        cancelText="Отмена"
        okButtonProps={{ disabled: !effectivePeriod }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text>Будет сохранено {uploadResult?.rows_count} записей</Text>

          <Select
            style={{ width: '100%' }}
            placeholder="Выберите период"
            value={isNewPeriod ? '__new__' : selectedPeriod}
            onChange={(value) => {
              if (value === '__new__') {
                setIsNewPeriod(true)
                setSelectedPeriod(null)
              } else {
                setIsNewPeriod(false)
                setSelectedPeriod(value)
              }
            }}
            options={periodOptions}
          />

          {isNewPeriod && (
            <Space>
              <Select
                style={{ width: 150 }}
                value={newMonth}
                onChange={setNewMonth}
                options={generateMonthOptions()}
              />
              <Input
                style={{ width: 80 }}
                placeholder="ГГ"
                value={newYear}
                onChange={(e) => setNewYear(e.target.value.replace(/\D/g, '').slice(0, 2))}
                maxLength={2}
                addonBefore="20"
              />
              <Text type="secondary">
                Период: {newMonth}{newYear}
              </Text>
            </Space>
          )}

          {periodCheck?.exists && !isNewPeriod && (
            <Alert
              message={`За ${periodCheck.period_label} уже есть ${periodCheck.count} записей`}
              type="warning"
              showIcon
            />
          )}
        </Space>
      </Modal>

      {/* Modal подтверждения замены */}
      <Modal
        title="Подтверждение замены данных"
        open={confirmReplaceOpen}
        onCancel={() => setConfirmReplaceOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setConfirmReplaceOpen(false)}>
            Отмена
          </Button>,
          <Button key="add" onClick={() => doSave(false)}>
            Добавить к существующим
          </Button>,
          <Button key="replace" type="primary" danger onClick={() => doSave(true)}>
            Заменить все
          </Button>,
        ]}
      >
        <Alert
          message={`За период ${periodCheck?.period_label} уже есть ${periodCheck?.count} записей в базе данных`}
          description="Выберите действие:"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li><Text strong>Добавить к существующим</Text> — новые записи добавятся, существующие обновятся</li>
          <li><Text strong type="danger">Заменить все</Text> — существующие записи будут удалены и заменены новыми</li>
        </ul>
      </Modal>
    </div>
  )
}
